import { useId } from "react";
import { BONES as B, GROUND } from "./rig";
import { CHARACTERS } from "./characters";
import { add, down, r, SOLE_PROBES } from "./geom";
import { HeadBack, HeadFront, HeadProfile } from "./heads";
import { Arm, Backpack, CapeBack, Defs, HandProps, LegFront, LegProfile, Skirt, Torso } from "./parts";

function solveProfile(p) {
  const legAt = (hip, l) => {
    const knee = add(hip, down(l.h), B.thigh), a = l.h - l.k, ankle = add(knee, down(a), B.shin);
    const base = [Math.cos(r(a)), -Math.sin(r(a))], c = Math.cos(r(-l.d)), s = Math.sin(r(-l.d));
    const f = [base[0] * c - base[1] * s, base[0] * s + base[1] * c], n = [-f[1], f[0]];
    const T = ([u, v]) => [ankle[0] + f[0] * u + n[0] * v, ankle[1] + f[1] * u + n[1] * v];
    return { hip, knee, ankle, T, low: Math.max(...SOLE_PROBES.map((q) => T(q)[1])) };
  };
  const probe = Math.max(legAt([0, 0], p.L).low, legAt([0, 0], p.R).low);
  const pelvis = [150 + p.px, GROUND - probe + p.py];
  const up = [Math.sin(r(p.torso)), -Math.cos(r(p.torso))], neck = add(pelvis, up, B.torso), sh = add(neck, up, -10);
  const armAt = (a) => { const el = add(sh, down(a.s), B.upperArm); return { sh, el, wr: add(el, down(a.s + a.e), B.foreArm) }; };
  return { pelvis, up, neck, legs: { L: legAt(add(pelvis, [-5, 0]), p.L), R: legAt(pelvis, p.R) }, arms: { L: armAt(p.aL), R: armAt(p.aR) }, head: add(add(neck, up, 33), [4, 0]) };
}

/** Front/back solve. Front is a true mirror (character's right on screen left); back keeps right on screen right. */
export function solveFront(p, mirror) {
  const legAt = (sgn, l) => {
    const hip0 = [sgn * B.hipHalf, 0], lat = sgn * Math.sin(r(l.abd));
    const knee0 = add(hip0, [lat * B.thigh, Math.cos(r(l.h)) * B.thigh]);
    const ankle0 = add(knee0, [lat * B.shin * 0.4, Math.abs(Math.cos(r(l.h - l.k))) * B.shin]);
    return { hip0, knee0, ankle0, low: ankle0[1] + 23 };
  };
  const m = mirror ? 1 : -1, L = legAt(-m, p.L), R = legAt(m, p.R);
  const pelvis = [150 + p.px * m, GROUND - Math.max(L.low, R.low) + p.py];
  const up = [Math.sin(r(p.torso * m)), -Math.cos(r(p.torso * m))], neck = add(pelvis, up, B.torso), n = [-up[1], up[0]];
  const mk = (g, sgn) => ({ hip: add(pelvis, g.hip0), knee: add(pelvis, g.knee0), ankle: add(pelvis, g.ankle0), sgn });
  const armAt = (sgn, a) => { const sh = add(add(neck, up, -12), n, sgn * B.shoulderHalf); const el = add(sh, [sgn * Math.sin(r(a.a)), Math.cos(r(a.a))], B.upperArm); return { sh, el, wr: add(el, [sgn * Math.sin(r(a.a + a.e * 0.4)), Math.cos(r(a.a + a.e * 0.4))], B.foreArm) }; };
  return { pelvis, up, neck, legs: { L: mk(L, -m), R: mk(R, m) }, arms: { L: armAt(-m, p.aL), R: armAt(m, p.aR) }, head: add(neck, up, 35) };
}

const Profile = ({ g, ch, pid }) => (
  <>
    <Backpack g={g} ch={ch} view="profile" /><CapeBack g={g} ch={ch} view="profile" />
    <Arm a={g.arms.L} far ch={ch} /><LegProfile g={g.legs.L} far ch={ch} pid={pid} />
    <Torso g={g} ch={ch} view="profile" pid={pid} /><LegProfile g={g.legs.R} ch={ch} pid={pid} />
    <Skirt g={g} ch={ch} view="profile" pid={pid} />
    <HeadProfile c={g.head} ch={ch} /><HandProps g={g} ch={ch} view="profile" pid={pid} layer="under" />
    <Arm a={g.arms.R} ch={ch} /><HandProps g={g} ch={ch} view="profile" pid={pid} />
  </>
);

const Front = ({ g, ch, pid, back }) => (
  <>
    {!back && <CapeBack g={g} ch={ch} view="front" />}
    <LegFront g={g.legs.L} ch={ch} pid={pid} back={back} /><LegFront g={g.legs.R} ch={ch} pid={pid} back={back} />
    <Skirt g={g} ch={ch} view="front" pid={pid} />
    <HandProps g={g} ch={ch} view={back ? "back" : "front"} pid={pid} layer="under" />
    <Arm a={g.arms.L} ch={ch} /><Arm a={g.arms.R} ch={ch} />
    <Torso g={g} ch={ch} view={back ? "back" : "front"} pid={pid} />
    {back ? <><Backpack g={g} ch={ch} view="back" /><CapeBack g={g} ch={ch} view="back" /><HeadBack c={g.head} ch={ch} /></> : <HeadFront c={g.head} ch={ch} />}
    <HandProps g={g} ch={ch} view="front" pid={pid} />
  </>
);

/** Full-body character on Bao's shared skeleton. view: "profile" | "front" | "back". character: key of CHARACTERS. */
export const BaoRig = ({ pose, view = "profile", width = 300, shadow = true, testId, character = "bao" }) => {
  const ch = CHARACTERS[character] || CHARACTERS.bao, pid = useId().replace(/:/g, "");
  const p = { ...pose, torso: pose.torso + (ch.posture || 0) };
  const g = view === "profile" ? solveProfile(p) : solveFront(p, view === "back");
  const lift = Math.max(0, GROUND - Math.max(g.legs.L.ankle[1], g.legs.R.ankle[1]) - 22);
  return (
    <svg viewBox="0 -40 300 460" width={width} height={typeof width === "number" ? width * (460 / 300) : undefined} role="img" aria-label={`${ch.name} · vue ${view === "front" ? "de face" : view === "back" ? "de dos" : "de profil"}`} data-testid={testId}>
      <Defs ch={ch} pid={pid} />
      {shadow && <ellipse cx={g.pelvis[0]} cy={GROUND + 2} rx={Math.max(26, 62 - lift * 0.5) * ch.scale} ry="7" fill="#0A2540" opacity={Math.max(0.08, 0.22 - lift * 0.002)} />}
      <g transform={`translate(150 ${GROUND}) scale(${ch.scale}) translate(-150 ${-GROUND})`}>
        {view === "profile" ? <Profile g={g} ch={ch} pid={pid} /> : <Front g={g} ch={ch} pid={pid} back={view === "back"} />}
      </g>
    </svg>
  );
};
